import type {ActionTypes} from './createActionTypes';
import PropTypes from 'prop-types';
import Sort from '../../../shared/constants/CRUD'

export type CrudAction = { type: string, payload: {} | string };

export type Actions = {
    clear: PropTypes.func,
    select: PropTypes.func,
    get: PropTypes.func,
    getSuccess: PropTypes.func,
    getFailed: PropTypes.func,
    listFirstTime: PropTypes.func,
    list: PropTypes.func,
    listSuccess: PropTypes.func,
    listFailed: PropTypes.func,
    create: PropTypes.func,
    createSuccess: PropTypes.func,
    createFailed: PropTypes.func,
    update: PropTypes.func,
    updateSuccess: PropTypes.func,
    updateFailed: PropTypes.func,
    deleteResources: PropTypes.func,
    deleteSuccess: PropTypes.func,
    deleteFailed: PropTypes.func,
    show: PropTypes.func,
    showSuccess: PropTypes.func,
    showFailed: PropTypes.func,
    exportResources: PropTypes.func,
    exportSuccess: PropTypes.func,
    exportFailed: PropTypes.func,
    exportBigData: PropTypes.func,
    exportBigDataSuccess: PropTypes.func,
    exportBigDataFailed: PropTypes.func,
    downloadFromURL: PropTypes.func,
    downloadFromURLSuccess: PropTypes.func,
    downloadFromURLFailed: PropTypes.func,
    exportBigDataSuccess: PropTypes.func,
    exportBigDataFailed: PropTypes.func,
    exportDetail: PropTypes.func,
    exportDetailSuccess: PropTypes.func,
    exportDetailFailed: PropTypes.func,
    upload: PropTypes.func,
    uploadSuccess: PropTypes.func,
    uploadFailed: PropTypes.func,
    downloadTemplate: PropTypes.func,
    downloadTemplateSuccess: PropTypes.func,
    downloadTemplateFailed: PropTypes.func,
};

export function createActions(actionTypes: ActionTypes): Actions {
    const clear = (): CrudAction => ({
        type: actionTypes.CLEAR,
        payload: {},
    });
    const select = (selected): Action => ({
        type: actionTypes.SELECT,
        payload: selected,
    });
    const get = (id: number): CrudAction => ({
        type: actionTypes.GET,
        payload: {id},
    });
    const getSuccess = (data): CrudAction => ({
        type: actionTypes.GET_SUCCESS,
        payload: data,
    });
    const getFailed = (error: string): CrudAction => ({
        type: actionTypes.GET_FAILED,
        payload: error,
    });
    
    const listFirstTime = (page: number, sizePerPage: number, searchText: string, sort: Sort, filters: Object, url?: string): CrudAction => ({
        type: actionTypes.LIST_FIRST_TIME,
        payload: {page, sizePerPage, searchText, sort, filters, url},
    });

    const list = (page: number, sizePerPage: number, searchText: string, sort: Sort, filters: Object, url?: string, customParams?: Object): CrudAction => ({
        type: actionTypes.LIST,
        payload: {page, sizePerPage, searchText, sort, filters, url, customParams},
    });

    const listSuccess = (entities: [], page: number, sizePerPage: number, searchText: string, sort: Sort, filters: Object, total: number): CrudAction => ({
        type: actionTypes.LIST_SUCCESS,
        payload: {entities, page, sizePerPage, searchText, sort, filters, total},
    });

    const listFailed = (error: string): CrudAction => ({
        type: actionTypes.LIST_FAILED,
        payload: error,
    });

    const create = (data): CrudAction => ({
        type: actionTypes.CREATE,
        payload: data,
    });

    const createSuccess = (result: {}): CrudAction => ({
        type: actionTypes.CREATE_SUCCESS,
        payload: result,
    });

    const createFailed = (error: string): CrudAction => ({
        type: actionTypes.CREATE_FAILED,
        payload: error,
    });

    const deleteResources = (payload: {} | []): CrudAction => ({
        type: actionTypes.DELETE,
        payload,
    });

    const deleteSuccess = (result: string): CrudAction => ({
        type: actionTypes.DELETE_SUCCESS,
        payload: {result},
    });

    const deleteFailed = (error: string): CrudAction => ({
        type: actionTypes.DELETE_FAILED,
        payload: error,
    });

    const update = (payload: {} | []): CrudAction => ({
        type: actionTypes.UPDATE,
        payload,
    });

    const updateSuccess = (result: {}): CrudAction => ({
        type: actionTypes.UPDATE_SUCCESS,
        payload: result,
    });

    const updateFailed = (error: string): CrudAction => ({
        type: actionTypes.UPDATE_FAILED,
        payload: error,
    });

    const show = (payload: {}): CrudAction => ({
        type: actionTypes.SHOW,
        payload,
    });

    const showSuccess = (result: {}): CrudAction => ({
        type: actionTypes.SHOW_SUCCESS,
        payload: result,
    });

    const showFailed = (error: string): CrudAction => ({
        type: actionTypes.SHOW_FAILED,
        payload: error,
    });

    const exportResources = (filters): CrudAction => ({
        type: actionTypes.EXPORT,
        payload: {filters},
    });

    const exportSuccess = (response: {}): CrudAction => ({
        type: actionTypes.EXPORT_SUCCESS,
        payload: response,
    });

    const exportFailed = (error: string): CrudAction => ({
        type: actionTypes.EXPORT_FAILED,
        payload: error,
    });

    const exportBigData = (key: string, filters, zip?: boolean = false): CrudAction => ({
        type: actionTypes.EXPORT_BIG_DATA,
        payload: {key, filters, zip},
    });

    const exportBigDataSuccess = (response: {}): CrudAction => ({
        type: actionTypes.EXPORT_BIG_DATA_SUCCESS,
        payload: response,
    });

    const exportBigDataFailed = (error: string): CrudAction => ({
        type: actionTypes.EXPORT_BIG_DATA_FAILED,
        payload: error,
    });

    const downloadFromURL = (url): CrudAction => ({
        type: actionTypes.DOWNLOAD_FROM_URL,
        payload: {url},
    });

    const downloadFromURLSuccess = (response: {}): CrudAction => ({
        type: actionTypes.DOWNLOAD_FROM_URL_SUCCESS,
        payload: response,
    });

    const downloadFromURLFailed = (error: string): CrudAction => ({
        type: actionTypes.DOWNLOAD_FROM_URL_FAILED,
        payload: error,
    });

    const exportDetail = (filters): CrudAction => ({
        type: actionTypes.EXPORT_DETAIL,
        payload: {filters},
    });

    const exportDetailSuccess = (response: {}): CrudAction => ({
        type: actionTypes.EXPORT_DETAIL_SUCCESS,
        payload: response,
    });

    const exportDetailFailed = (error: string): CrudAction => ({
        type: actionTypes.EXPORT_DETAIL_FAILED,
        payload: error,
    });

    const upload = (file): CrudAction => ({
        type: actionTypes.UPLOAD,
        payload: {file},
    });

    const uploadSuccess = (response: {}): CrudAction => ({
        type: actionTypes.UPLOAD_SUCCESS,
        payload: response,
    });

    const uploadFailed = (error: string): CrudAction => ({
        type: actionTypes.UPLOAD_FAILED,
        payload: error,
    });

    const downloadTemplate = (payload): CrudAction => ({
        type: actionTypes.DOWNLOAD_TEMPLATE,
        payload,
    });

    const downloadTemplateSuccess = (response: {}): CrudAction => ({
        type: actionTypes.DOWNLOAD_TEMPLATE_SUCCESS,
        payload: response,
    });

    const downloadTemplateFailed = (error: string): CrudAction => ({
        type: actionTypes.DOWNLOAD_TEMPLATE_FAILED,
        payload: error,
    });

    return {
        clear, select,
        get, getSuccess, getFailed,
        listFirstTime, list, listSuccess, listFailed,
        create, createSuccess, createFailed,
        update, updateSuccess, updateFailed,
        deleteResources, deleteSuccess, deleteFailed,
        show, showSuccess, showFailed,
        exportResources, exportSuccess, exportFailed,
        exportBigData, exportBigDataSuccess, exportBigDataFailed,
        downloadFromURL, downloadFromURLSuccess, downloadFromURLFailed,
        exportDetail, exportDetailSuccess, exportDetailFailed,
        upload, uploadSuccess, uploadFailed,
        downloadTemplate, downloadTemplateSuccess, downloadTemplateFailed,
    }
}
